<template>
  <div
    v-if="showServicesList"
    id="services-list-div"
    class="push-me-downwards border pt-1 px-1"
  >
    <b-container class="px-0">
      <b-row>
        <b-col
          cols="7"
          class="right"
        >
          <b-button
            v-if="canCreateServices"
            v-show="showServiceSearch"
            variant="primary"
            size="sm"
            class="mb-1"
            @click="openServiceModal()"
          >
            <span>Create new service</span>
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-container v-if="services.length > 0">
      <b-row class="list-services-header-row">
        <b-col cols="1" class="p-0 mx-0">&nbsp;</b-col>
        <b-col cols="4" class="p-0 mx-0 text-center">
          <b class="text-white my-1">
            Service
          </b>
        </b-col>
        <b-col cols="2" class="p-0 mx-0 text-center">
          <b class="text-white my-1">
            Price
          </b>
        </b-col>
        <b-col cols="3" class="p-0 mx-0 text-center">
          <b class="text-white my-1">
            Quantity
          </b>
        </b-col>
        <b-col cols="2" class="p-0 mx-0 text-center">
          <b class="text-white my-1">
            Total
          </b>
        </b-col>
      </b-row>

      <b-row
        v-for="service in services"
        :key="service.id"
        class="py-1 my-1 selected-services-row"
      >
        <template v-if="service.items_quantity > 0">
          <b-col cols="1" class="p-0 mx-0">
            <img
              :src="servicePictureSrc(service)"
              :style="{ width: '100%', border: '1px solid #DDDDDD' }"
              class="small-horizontal-padding"
            >
          </b-col>
          <b-col cols="4" class="p-0 mx-0">
            <span
              class="mt-2"
              :class="{ 'deleted-service-name': service.deleted }"
            >{{ service.name }}</span>
          </b-col>
          <b-col cols="2" class="p-0 mx-0 text-center">
            <span class="mt-2">{{ $formatCurrency(service.price, null) }}</span>
          </b-col>

          <b-col
            cols="3"
            class="text-center p-0 mx-0"
          >
            <vue-number-input
              inline
              controls
              :readonly="isPlatformSale || servicesReadOnly || service.deleted"
              :model-value="service.items_quantity"
              :min="0"
              @update:modelValue="updateServiceQuantity($event, service.id, service)"
            />
          </b-col>
          <b-col cols="2" class="p-0 mx-0 text-center">
            <span class="mt-2">{{ $formatCurrency(service.items_quantity * service.price) }}</span>
          </b-col>
        </template>
      </b-row>
    </b-container>

    <b-container class="px-0">
      <b-row
        v-show="showServiceSearch"
        class="py-2"
      >
        <b-col aria-colspan="6">
          <div class="d-flex align-items-center services-search-row">
            <b-form-input
              v-model="search_keyword"
              placeholder="Search service by name"
              class="services-search-input"
            />
            <b-button
              type="button"
              variant="outline-secondary"
              size="sm"
              class="ml-1 flex-shrink-0"
              @click="clearSearch"
            >
              Clean
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="searchServices.length > 0"
        class="search-products-header-row border-radius-bottom"
      >
        <b-col cols="1">&nbsp;</b-col>
        <b-col cols="4">
          <span class="my-1">Service</span>
        </b-col>
        <b-col cols="5">
          <span class="my-1">Price</span>
        </b-col>
        <b-col cols="2">
          <span class="my-1 text-center">&nbsp;</span>
        </b-col>
      </b-row>
      <div
        v-show="searchServices.length > 0"
        class="scrollable-container"
      >
        <b-row
          v-for="(service, index) in searchServices"
          :key="service.id"
          class="pb-1 m-1"
          :class="{ 'border-bottom': index < searchServices.length - 1 }"
        >
          <b-col cols="1" class="p-0">
            <img
              :src="servicePictureSrc(service)"
              :style="{ width: '100%', border: '1px solid #DDDDDD' }"
            >
          </b-col>
          <b-col cols="4">
            <span class="mt-2">{{ service.name }}</span>
          </b-col>
          <b-col cols="5">
            <span class="mt-2">{{ $formatCurrency(service.price, null) }}</span>
          </b-col>
          <b-col
            cols="2"
            class="text-right p-0"
          >
            <b-button
              type="button"
              variant="dark"
              size="sm"
              @click="addServiceFromSearch(service)"
            >
              Add
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-container>
  </div>
  <ManageService
    :click-date="clickDate"
    :service="null"
    @service-created="addCreatedServiceToOrder"
  />
</template>

<script>
import { watch, ref, computed, provide } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCol,
  BFormInput,
  BContainer,
  BRow,
} from 'bootstrap-vue'
import VueNumberInput from '@chenfengyuan/vue-number-input'
import ManageService from '@/views/Components/ManageService.vue'
import { ORDER_SERVICE_ADD_KEY } from '@/constants/injectionKeys'
import { canStaffCreate } from '@/libs/staffPermissions'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  name: 'OrderServices',
  components: {
    ManageService,
    BButton,
    BCol,
    BFormInput,
    BContainer,
    BRow,
    VueNumberInput,
  },
  props: {
    services: {
      type: Array,
      required: true,
    },
    sale: {
      type: Object,
      default: null,
    },
    servicesReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:services'],
  setup(props, { emit }) {
    const store = useStore()
    const search_keyword = ref('')
    const searchServices = ref([])
    const clickDate = ref(null)

    const isPlatformSale = computed(() => Boolean(props.sale?.platform))
    const servicesReadOnly = computed(() => Boolean(props.servicesReadOnly))
    const showServiceSearch = computed(() => {
      if (servicesReadOnly.value) {
        return false
      }
      return !props.sale || !props.sale.platform
    })
    const hasActiveServices = computed(() => (
      (props.services || []).some((service) => Number(service.items_quantity) > 0)
    ))
    const showServicesList = computed(() => hasActiveServices.value || showServiceSearch.value)
    const canCreateServices = computed(() => canStaffCreate(store.getters['auth/user'], 'services'))

    const serviceIdEquals = (a, b) => String(a) === String(b)

    const normalizePicture = (picture) => {
      if (Array.isArray(picture) && picture[0]?.src) {
        return picture
      }
      if (typeof picture === 'string' && picture.trim() !== '') {
        try {
          const parsed = JSON.parse(picture)
          if (Array.isArray(parsed) && parsed[0]?.src) {
            return parsed
          }
        } catch (e) {
          // not JSON
        }
      }
      return [{ name: '', src: NO_IMAGE }]
    }

    const servicePictureSrc = (service) => {
      const picture = normalizePicture(service?.picture)
      return picture[0]?.src || NO_IMAGE
    }

    const buildCartService = (serviceId, newQuantity, source) => {
      const data = source?.data ?? source

      return {
        id: serviceId,
        items_quantity: newQuantity,
        name: data.name,
        price: data.price,
        picture: normalizePicture(data.picture),
        deleted: Boolean(data.deleted),
      }
    }

    const setServices = (nextServices) => {
      emit('update:services', nextServices)
    }

    const removeFromSearchResults = (serviceId) => {
      searchServices.value = searchServices.value.filter((s) => !serviceIdEquals(s.id, serviceId))
    }

    const addServiceFromSearch = (searchService) => {
      if (servicesReadOnly.value || !searchService?.id) {
        return
      }

      const existing = props.services.find((s) => serviceIdEquals(s.id, searchService.id))
      const currentQty = existing ? parseInt(existing.items_quantity, 10) : 0
      const nextQty = Number.isNaN(currentQty) ? 1 : currentQty + 1

      if (existing) {
        setServices(
          props.services.map((s) => (
            serviceIdEquals(s.id, searchService.id)
              ? { ...s, items_quantity: nextQty }
              : s
          )),
        )
        removeFromSearchResults(searchService.id)
        return
      }

      setServices([
        ...props.services,
        buildCartService(searchService.id, nextQty, searchService),
      ])
      removeFromSearchResults(searchService.id)
    }

    const clearSearch = () => {
      search_keyword.value = ''
      searchServices.value = []
    }

    const updateServiceQuantity = (newQuantity, serviceId, sourceService = null) => {
      if (servicesReadOnly.value) {
        return false
      }

      const parsedQuantity = parseInt(newQuantity, 10)

      if (Number.isNaN(parsedQuantity)) {
        return false
      }

      const cartService = props.services.find((s) => serviceIdEquals(s.id, serviceId))
      const searchService = searchServices.value.find((s) => serviceIdEquals(s.id, serviceId))

      if (cartService) {
        if (parsedQuantity === 0) {
          setServices(props.services.filter((s) => !serviceIdEquals(s.id, serviceId)))
        } else {
          setServices(
            props.services.map((s) => (
              serviceIdEquals(s.id, serviceId)
                ? { ...s, items_quantity: parsedQuantity }
                : s
            )),
          )
        }
        return true
      }

      if (parsedQuantity > 0) {
        const fromSearch = sourceService || searchService
        if (fromSearch) {
          setServices([
            ...props.services,
            buildCartService(serviceId, parsedQuantity, fromSearch),
          ])
          return true
        }

        store.dispatch('services/loadService', serviceId)
          .then((response) => {
            const serviceData = response?.data ?? response
            setServices([
              ...props.services,
              buildCartService(serviceId, parsedQuantity, serviceData),
            ])
          })
          .catch(() => {})
      }

      return false
    }

    const addCreatedServiceToOrder = (createdService) => {
      const service = createdService?.data ?? createdService

      if (!service?.id || servicesReadOnly.value) {
        return
      }

      searchServices.value = [
        service,
        ...searchServices.value.filter((s) => !serviceIdEquals(s.id, service.id)),
      ]
      search_keyword.value = service.name || String(service.id)

      const cartService = buildCartService(service.id, 1, service)
      const existing = props.services.find((s) => serviceIdEquals(s.id, service.id))

      if (existing) {
        setServices(
          props.services.map((s) => (
            serviceIdEquals(s.id, service.id)
              ? { ...cartService, items_quantity: 1 }
              : s
          )),
        )
        return
      }

      setServices([...props.services, cartService])
    }

    provide(ORDER_SERVICE_ADD_KEY, addCreatedServiceToOrder)

    watch(
      () => store.state.services.orderCreatedService,
      (service) => {
        if (!service?.id) {
          return
        }

        addCreatedServiceToOrder(service)
        store.commit('services/SET_ORDER_CREATED_SERVICE', null)
      },
    )

    const openServiceModal = () => {
      clickDate.value = new Date()
    }

    const parseSearchServices = (response) => {
      if (Array.isArray(response)) {
        return response
      }
      if (Array.isArray(response?.data)) {
        return response.data
      }
      return []
    }

    watch(search_keyword, (keyword) => {
      if (servicesReadOnly.value) {
        searchServices.value = []
        return
      }

      const trimmed = String(keyword || '').trim()
      if (trimmed.length < 1) {
        searchServices.value = []
        return
      }

      store.dispatch('services/searchService', trimmed)
        .then((response) => {
          if (String(search_keyword.value || '').trim() !== trimmed) {
            return
          }

          searchServices.value = parseSearchServices(response)
        })
        .catch(() => {
          if (String(search_keyword.value || '').trim() === trimmed) {
            searchServices.value = []
          }
        })
    })

    return {
      search_keyword,
      searchServices,
      clickDate,
      isPlatformSale,
      servicesReadOnly,
      showServiceSearch,
      showServicesList,
      canCreateServices,
      servicePictureSrc,
      addServiceFromSearch,
      addCreatedServiceToOrder,
      updateServiceQuantity,
      openServiceModal,
      clearSearch,
    }
  },
}
</script>

<style scoped>
.services-search-row {
  width: 100%;
}

.services-search-input {
  flex: 1 1 auto;
  min-width: 0;
}

.deleted-service-name {
  text-decoration: line-through;
  color: #6e6b7b;
}

@media (max-width: 767.98px) {
  #services-list-div {
    font-size: 0.75rem;
  }

  #services-list-div :deep(h5) {
    font-size: 0.8rem;
    margin-top: 0.35rem !important;
    margin-bottom: 0.35rem !important;
  }

  #services-list-div :deep(.form-control),
  #services-list-div :deep(.btn),
  #services-list-div :deep(small) {
    font-size: 0.7rem;
  }

  #services-list-div :deep(.vue-number-input__input) {
    font-size: 0.75rem;
    min-width: 2.25rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  #services-list-div :deep(.vue-number-input--inline > .vue-number-input__input) {
    width: 8.5rem;
  }

  #services-list-div :deep(.vue-number-input--controls > .vue-number-input__button) {
    width: 1.75rem;
  }

  #services-list-div :deep(.vue-number-input--controls > .vue-number-input__input) {
    padding-left: 2.1rem;
    padding-right: 2.1rem;
  }
}
</style>
